import type { V1CompletedRunStateOutputs } from '@/generated/hooks/bishop/models';
import type { HTMLAttributes, ReactNode } from 'react';
import type { IconType } from '@kognitos/lattice';
import type { IIDPField } from '../idp/types';

// ============================================================================
// Render Mode Enum
// ============================================================================

/**
 * Rendering mode for outputs
 * - compact: Minimal space, single line where possible
 * - preview: Medium detail, good for cards/lists
 * - expanded: Full detail, all information visible
 */
export enum ERenderMode {
  COMPACT = 'compact',
  PREVIEW = 'preview',
  EXPANDED = 'expanded',
}

/** Output-card geometry selected by the surface that renders it. */
export enum EOutputCardSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

/** Visual/content treatment inferred from an output's resolved value. */
export enum EOutputCardVariant {
  TEXT = 'text',
  NUMBER = 'number',
  LIST = 'list',
  TABLE = 'table',
  IDP = 'idp',
  FILE = 'file',
  CODE = 'code',
  BROWSER = 'browser',
  DICT = 'dict',
  EMPTY = 'empty',
}

/**
 * Which reading of an output the preview modal shows, for any output whose
 * raw text is available alongside its rendered form (HTML, or an inline CSV
 * table).
 */
export enum EOutputSourceView {
  PREVIEW = 'preview',
  SOURCE = 'source',
}

// ============================================================================
// Resolved Value Types
// ============================================================================

/**
 * Primitive resolved types - all simple values are converted to these
 */
export type TResolvedPrimitive = null | number | string;

/**
 * Specialized table type - discriminated by remote vs inline
 */
export type TResolvedTable =
  | { type: 'table-remote'; tableUrl: string }
  | { type: 'table-inline'; data: string };

/**
 * Specialized file type - discriminated by remote vs inline
 */
export type TResolvedFile =
  | { type: 'file-remote'; fileUrl: string }
  | { type: 'file-inline'; name: string; content: string };

/**
 * The set of value kinds that can be offloaded to a remote file.
 * Maps 1:1 to `PaginateValueContentValueKind` on the API side.
 */
export type TRemoteValueKind = 'text' | 'list' | 'set' | 'dictionary';

/**
 * Marker for an offloaded `Commonv1Value` whose content lives in a separate
 * file and must be fetched via `paginateValueContent`. Produced by
 * `resolveCommonValue` when a wrapper (`text_value`, `list_value`, `set_value`,
 * `dictionary_value`) carries a `remote` resource name instead of `inline` data.
 *
 * Kept as a top-level marker variant (alongside `TResolvedFile`/`TResolvedTable`)
 * so primitives stay bare and downstream helpers can treat it as a complex
 * object without special-casing primitive types.
 */
export type TResolvedRemoteValue = {
  type: 'remote-value';
  kind: TRemoteValueKind;
  /** Resource name of the form `organizations/{org}/workspaces/{ws}/files/{file}`. */
  resource: string;
};

/**
 * Main resolved value type - recursive for nested structures
 */
export type TResolvedValue =
  | TResolvedPrimitive
  | TResolvedTable
  | TResolvedFile
  | TResolvedRemoteValue
  | IIDPField
  | TResolvedValue[]
  | { [key: string]: TResolvedValue };

/**
 * Result type for V1NodeExecutedOutputs - a dictionary of resolved values
 */
export type TResolvedOutputs = { [key: string]: TResolvedValue };

// ============================================================================
// Metadata Types
// ============================================================================

/**
 * Metadata item for preview layover sidebar
 * Contains label, type, and the actual value for each item
 */
export interface IMetadataItem {
  label: string; // e.g., "[0]", "username", "Invoice"
  type: string; // e.g., "text", "number", "array", "dictionary", "table", "file"
  value: TResolvedValue; // the actual value
  fileName?: string; // optional file name for file types
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for OutputCardWrapper component
 */
export interface IOutputCardWrapperProps {
  name: string;
  children: ReactNode;
  horizontal?: boolean;
  onExpand?: () => void;
  /** Optional download handler - when provided, shows a download button in the card header */
  onDownload?: () => void;
  iconType: IconType; // Icon type for the output (e.g., 'Type', 'Hash', 'List')
  /** Optional display name override - when provided, shown instead of formatDisplayName(name) */
  displayName?: string;
  config?: {
    disableScroll?: boolean;
    /**
     * Suppresses the "value is clipped" hover tooltip. Set for rich previews
     * (files, IDP documents) whose tooltip would mount a second copy of the
     * renderer rather than re-present a clipped string.
     */
    disableValueTooltip?: boolean;
    /** Lets aspect-ratio renderers determine the card height. */
    fitContent?: boolean;
  };
  /** Callback ref for the scrollable value area, used as the PDF scroll parent. */
  scrollAreaRef?: (el: HTMLDivElement | null) => void;
  /** Extra actions rendered after the standard header actions. */
  headerActions?: ReactNode;
  /** Rendered right after the title, e.g. a status badge. */
  titleAdornment?: ReactNode;
  /** Props for the card header when the surrounding surface owns an interaction. */
  headerProps?: TOutputCardHeaderProps;
}

/** A minimal browser preview's status, shown by the host next to its title. */
export type TBrowserPreviewStatus = 'live' | 'replay' | null;

type TOutputCardHeaderProps = HTMLAttributes<HTMLDivElement> & {
  'data-testid'?: string;
};

/**
 * Props for OutputCard component
 */
export interface IOutputCardProps {
  /** The key/name of the output */
  name: string;
  /** The resolved value of the output */
  value: TResolvedValue;
  /** Surface-driven card size. Defaults to md; sm must be selected by a table. */
  size?: EOutputCardSize;
  /** @deprecated Prefer `size`; retained for source compatibility only. */
  horizontal?: boolean;
  /** Callback when expand button is clicked */
  onExpand?: (name: string, value: TResolvedValue) => void;
  /** Optional download handler from parent - called with (name, value) */
  onDownload?: (name: string, value: TResolvedValue) => void;
  /** Optional display name override - shown instead of formatDisplayName(name) */
  displayName?: string;
  /** Hides the bespoke output glyph without changing the remaining layout. */
  showIcon?: boolean;
  /** Hides the lg title row when the surrounding surface supplies its own label. */
  showTitle?: boolean;
  /** Optional copy callback. The serialized value is provided as the third argument. */
  onCopy?: (
    name: string,
    value: TResolvedValue,
    serializedValue: string,
  ) => void;
  /** Extra actions rendered after the standard header actions. */
  headerActions?: ReactNode;
  /** Props for the large-card header, for a surface-owned interaction. */
  headerProps?: TOutputCardHeaderProps;
}

/**
 * Props for OutputsSheet component
 */
export interface IOutputsSheetProps {
  outputs: V1CompletedRunStateOutputs;
  previewData: { name: string; value: TResolvedValue } | null;
  onPreviewChange: (
    data: { name: string; value: TResolvedValue } | null,
  ) => void;
  importantOutputs?: string[] | null;
  /**
   * When true, a detected browser session is rendered as a "Browser Operator
   * replay" card. Opt-in so chat surfaces (InlineOutputsSheet) — which already
   * show the session via ChatBrowserSessionWidget — don't render it twice.
   */
  renderBrowserSession?: boolean;
  /** Selects Run Assistant chrome while keeping every output lg-sized. */
  surface?: 'chat' | 'run';
}

/**
 * Props for OutputRenderer component
 */
export interface IOutputRendererProps {
  value: TResolvedValue;
  mode?: ERenderMode;
  onExpandedClose?: () => void;
  /** Output name used for download filenames */
  outputName?: string;
  /** Optional download handler - when provided, renderers show a download button */
  onDownload?: () => void;
  /** When set, nested PDF previews scroll within this element instead of an inner scroller. */
  scrollParent?: HTMLElement | null;
  /** Browser previews only: the bare screen with a status badge, no playback controls. */
  minimal?: boolean;
  /** File previews only: the host is a fixed-size tile the file should fill exactly. */
  fillContainer?: boolean;
}

/**
 * Props for PreviewLayover component
 */
export interface IPreviewLayoverProps {
  isOpen: boolean;
  outputName: string;
  outputValue: TResolvedValue;
  onClose: () => void;
  /** Optional download handler - called with (value, outputName) for the currently viewed item */
  onDownload?: (value: TResolvedValue, outputName: string) => void;
  /** When true, show a spinner inside the layover instead of `outputValue` —
   * lets a consumer open the (accessible) layover immediately while content loads. */
  isLoading?: boolean;
  /** Optional label (e.g. a filename) shown above the loading spinner. */
  loadingLabel?: string;
  /** Sizing overrides for the content box, e.g. `max-w-4xl max-h-[70vh]`.
   * Defaults to filling the padded viewport. */
  className?: string;
  /** Ordered peers available for previous/next navigation in the preview. */
  navigationItems?: IOutputPreviewItem[];
  /** Renders the header's close action. Defaults to true. Callers that supply
   * their own dismissal (a surrounding dialog, say) turn it off. */
  showCloseButton?: boolean;
}

/** One resolved output that can be inspected in the shared preview modal. */
export interface IOutputPreviewItem {
  name: string;
  value: TResolvedValue;
  /** Optional user-facing label when the output key is not the desired title. */
  displayName?: string;
}

// ============================================================================
// Renderer Props Types
// ============================================================================

/**
 * Props for PrimitiveRenderer component
 */
export interface IPrimitiveRendererProps {
  value: TResolvedPrimitive;
  mode: ERenderMode;
}

/**
 * Props for ArrayRenderer component
 */
export interface IArrayRendererProps {
  value: TResolvedValue[];
  renderValue: (value: TResolvedValue) => ReactNode;
  mode: ERenderMode;
}

/**
 * Props for DictionaryRenderer component
 */
export interface IDictionaryRendererProps {
  value: { [key: string]: TResolvedValue };
  renderValue: (value: TResolvedValue) => ReactNode;
  mode: ERenderMode;
  onExpandedClose?: () => void;
  /** Output name used for download filename */
  outputName?: string;
  /** Optional download handler - when provided, shows a download button */
  onDownload?: () => void;
}

/**
 * Props for TableRenderer component
 */
export interface ITableRendererProps {
  value: TResolvedTable;
  mode: ERenderMode;
  onExpandedClose?: () => void;
  outputName?: string;
  /** Optional download handler - when provided, shows a download button */
  onDownload?: () => void;
}

/**
 * Props for FileRenderer component
 */
export interface IFileRendererProps {
  /** Override format detection when a typed value determines the file format. */
  previewFileName?: string;
  /** Optional contextual error UI; retry is available only for remote files. */
  renderError?: (error: unknown, retry: (() => void) | undefined) => ReactNode;
  /** Toast when a remote file fails to load (default true). */
  notifyOnError?: boolean;
  /** Spinner while the file loads (default true); off when the host shows a skeleton. */
  showLoadingIndicator?: boolean;
  value: TResolvedFile;
  mode: ERenderMode;
  onExpandedClose?: () => void;
  testId?: string;
  /** Optional download handler - when provided, shows a download button */
  onDownload?: () => void;
  /** When set, PDF previews scroll within this element instead of an inner scroller. */
  scrollParent?: HTMLElement | null;
  /** Scale file thumbnails without changing the expanded viewer. */
  previewScale?: number;
  /** Previews only: the host is a fixed-size tile the file should fill exactly. */
  fillContainer?: boolean;
}

/**
 * Props for RemoteValueRenderer component
 */
export interface IRemoteValueRendererProps {
  value: TResolvedRemoteValue;
  mode: ERenderMode;
  onExpandedClose?: () => void;
  outputName?: string;
  /** Optional download handler - forwarded to the fetched inner renderer if applicable */
  onDownload?: () => void;
  /**
   * Re-enters the top-level `OutputRenderer` for the accumulated fetched value.
   * Supplied as a callback by the parent `OutputRenderer` to avoid a circular
   * import (the same pattern `ArrayRenderer` and `DictionaryRenderer` use for
   * their nested children).
   */
  renderValue: (value: TResolvedValue) => ReactNode;
}

/**
 * Props for BrowserRenderer component
 */
export interface IBrowserRendererProps {
  /** The browser instance value containing browser_name and a live view URL */
  value: import('../outputs/utils').IBrowserInstanceValue;
  /** Rendering mode (compact, preview, or expanded) */
  mode: ERenderMode;
  /** Optional callback when closing expanded view */
  onExpandedClose?: () => void;
  /** Optional label override for compact mode (defaults to the browser identifier). */
  label?: string;
  /**
   * Explicit override to treat the session as ended for replay even when the
   * live screencast is still connected. Normally this signal comes from
   * `BrowserReplayProvider` context (set while a run is paused for exception
   * guidance); pass this prop only for standalone/test use. When omitted, the
   * context value applies, defaulting to the screencast-driven behavior.
   */
  forceSessionEnded?: boolean;
  /** Preview mode only: the screen with a Live/Replay badge and Expand on hover. */
  minimal?: boolean;
  /** Opens the expanded view from the minimal preview. */
  onExpand?: () => void;
}

/**
 * File data structure - all files are treated as binary blobs
 */
export interface IFileData {
  name: string;
  blob: Blob;
  contentType?: string;
}
